import type { FC } from 'react';
import { Fragment } from 'react';

import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import cx from 'classnames';

export type ActionOption = {
  name: string;
  // biome-ignore lint/suspicious/noExplicitAny: Leaving this for backwards compatibility
  onClick: (node: any) => void;
  // biome-ignore lint/suspicious/noExplicitAny: Leaving this for backwards compatibility
  isVisible: (node: any) => boolean;
  isCurrent?: boolean;
};

export interface ActionsDropdownProps {
  size?: 'sm' | 'md';
  actions: ActionOption[][]; // Nested array of actions. Each nested array is a section of actions.
  buttonText?: string;
  // biome-ignore lint/suspicious/noExplicitAny: Leaving this for backwards compatibility
  node: any; // Some record/object the actions are being taken on. Passed into the isVisible and action functions.
  dropdownClass?: string;
  buttonClass?: string;
  optionClass?: string;
  disabled?: boolean;
}

const ActionsDropdown: FC<ActionsDropdownProps> = ({
  actions,
  buttonText = 'Actions',
  node,
  size = 'md',
  dropdownClass = 'right-0',
  buttonClass = '',
  optionClass = '',
  disabled,
}: ActionsDropdownProps) => {
  const isSmall = size === 'sm';

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button
          disabled={disabled}
          className={cx(
            buttonClass,
            'border-surface-200 z-0 inline-flex w-full justify-center whitespace-nowrap border bg-white text-sm font-medium text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75',
            isSmall ? 'px-2 py-1' : 'px-4 py-2',
          )}
        >
          {buttonText}
          <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5 text-gray-800" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={cx(
            'absolute z-10 w-56 origin-top-right divide-y divide-gray-100 bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
            dropdownClass,
          )}
        >
          {actions.map((section) => (
            <div className="px-1 py-1" key={JSON.stringify(section)}>
              {section.map((action) => {
                if (!action.isVisible(node)) {
                  return null;
                }

                const overrideOptionClass = action.name.split(' ').length === 1 && 'break-all';

                return (
                  <div key={action.name}>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          data-tip
                          data-for={action.name}
                          className={cx(
                            active ? 'bg-primary-100 text-primary-800' : 'text-gray-900',
                            action.isCurrent ? 'text-primary-800' : 'text-gray-900',
                            'group flex w-full items-center rounded-md px-2 py-2 text-sm',
                            optionClass,
                            overrideOptionClass,
                          )}
                          type="button"
                          onClick={() => action.onClick(node)}
                        >
                          {action.name}
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                );
              })}
            </div>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default ActionsDropdown;
